import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminSession } from '../../data/useAdminSession';
import { useCatalog } from '../../data/useCatalog';
import './Navbar.css';

function scrollToShop() {
  document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Navbar({ cartCount = 0, onSelectCategory, searchQuery = '', onSearchChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const shopJumpRef = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { categories } = useCatalog();
  const { authenticated } = useAdminSession();

  useEffect(() => {
    function handlePointerDown(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 140);
    return () => clearTimeout(timer);
  }, [searchOpen]);

  useEffect(() => {
    if (!shopJumpRef.current) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return undefined;
    }
    const timer = setTimeout(() => {
      shopJumpRef.current = false;
      scrollToShop();
    }, 80);
    return () => clearTimeout(timer);
  }, [pathname]);

  function handleHomeClick(event) {
    if (pathname !== '/') return;
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function selectCategory(id) {
    setMenuOpen(false);
    onSelectCategory?.(id);
    if (pathname === '/') {
      scrollToShop();
      return;
    }
    shopJumpRef.current = true;
    navigate('/');
  }

  function submitSearch(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    setSearchOpen(false);
    if (pathname !== '/') {
      shopJumpRef.current = true;
      navigate('/');
      return;
    }
    scrollToShop();
  }

  return (
    <header className="navbar">
      <div className="navbar__inner">
        <Link className="navbar__brand" to="/">
          <span className="navbar__wordmark">EVAR</span>
        </Link>

        <nav className="navbar__nav" aria-label="Main">
          <Link
            className={`navbar__trigger navbar__link${pathname === '/' ? ' navbar__link--active' : ''}`}
            to="/"
            aria-current={pathname === '/' ? 'page' : undefined}
            onClick={handleHomeClick}
          >
            Home
          </Link>

          <div className="navbar__item" ref={menuRef}>
            <button
              type="button"
              className="navbar__trigger"
              aria-expanded={menuOpen}
              aria-haspopup="true"
              onClick={() => setMenuOpen((open) => !open)}
            >
              Catalog
              <svg
                className={`navbar__caret${menuOpen ? ' navbar__caret--open' : ''}`}
                viewBox="0 0 12 12"
                width="12"
                height="12"
                aria-hidden="true"
              >
                <path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {menuOpen && (
              <ul className="navbar__dropdown">
                {categories.map((category) => (
                  <li key={category.id} className="navbar__group">
                    <button
                      type="button"
                      className="navbar__sublink"
                      onClick={() => selectCategory(category.id)}
                    >
                      {category.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="navbar__item navbar__item--search" ref={searchRef}>
            <button
              type="button"
              className={`navbar__trigger navbar__trigger--icon${searchOpen ? ' navbar__trigger--open' : ''}`}
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((open) => !open)}
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="M13.5 13.5 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>

            <div className={`navbar__search${searchOpen ? ' navbar__search--open' : ''}`}>
              <input
                ref={inputRef}
                className="navbar__input"
                type="search"
                placeholder="Search products"
                value={searchQuery}
                tabIndex={searchOpen ? 0 : -1}
                onChange={(event) => onSearchChange?.(event.target.value)}
                onKeyDown={submitSearch}
                dir="auto"
              />
            </div>
          </div>
        </nav>

        <div className="navbar__actions">
          {authenticated && (
            <Link
              className={`navbar__trigger navbar__link${pathname === '/admin' ? ' navbar__link--active' : ''}`}
              to="/admin"
              aria-current={pathname === '/admin' ? 'page' : undefined}
            >
              Admin
            </Link>
          )}

          <Link
            className="navbar__trigger navbar__trigger--icon navbar__cart"
            to="/checkout"
            aria-label={`Checkout, ${cartCount} items`}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <g
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.6 8h14.8l-1.1 12.1H5.7z" />
                <path d="M9 10.6V7a3 3 0 0 1 6 0v3.6" />
              </g>
            </svg>
            <span className="navbar__badge" aria-hidden="true" key={cartCount}>
              {cartCount}
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
